import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import type { UpdateFullNameDto } from '@api/models';
import { ModalResult } from '../../../../interfaces/modal-result.interface';
import { CustomerService } from '../../../../services/customer.service';
import { ButtonComponent } from '../../../button/button.component';
import { IconComponent } from '../../../icon/icon.component';
import { AutofocusDirective } from '../../../../directives/autofocus.directive';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

class UpdateFullNameForm {
  lastName: FormControl<string>;
  firstName: FormControl<string>;
  middleName: FormControl<string>;
}

@Component({
  selector: 'tc-update-full-name-modal',
  templateUrl: './update-full-name-modal.component.html',
  styleUrls: ['./update-full-name-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    IconComponent,
    ReactiveFormsModule,
    ButtonComponent,
    AutofocusDirective,
  ],
})
export class UpdateFullNameModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<UpdateFullNameModalComponent, ModalResult>);
  private destroyRef = inject(DestroyRef);
  private customerService = inject(CustomerService);

  form: FormGroup<UpdateFullNameForm>;

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'input' | 'success'>('input');
  isFormErrorsVisible = signal<boolean>(false);

  ngOnInit(): void {
    this.buildForm();
  }

  buildForm(): void {
    this.form = new FormGroup<UpdateFullNameForm>({
      lastName: new FormControl(this.customerService.customer().contactInfo.lastName || '', [Validators.required, Validators.minLength(2)]),
      firstName: new FormControl(this.customerService.customer().contactInfo.firstName || '', [Validators.required, Validators.minLength(2)]),
      middleName: new FormControl(this.customerService.customer().contactInfo.middleName || ''),
    });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.disableClose = true);
  }

  onSubmit(): void {
    this.error.set(null);
    this.isFormErrorsVisible.set(true);

    switch (this.state()) {
      case 'input':
        if (this.form.invalid) {
          return;
        }

        const dto: UpdateFullNameDto = {
          lastName: this.form.controls.lastName.getRawValue(),
          firstName: this.form.controls.firstName.getRawValue(),
          middleName: this.form.controls.middleName.getRawValue(),
        };

        this.isLoading.set(true);
        this.customerService.updateFullName(dto)
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.state.set('success');
              this.dialogRef.disableClose = false;
            },
            error: error => this.error.set(resolveHttpErrorMessage(error)),
          });
        break;


      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' });
        break;
    }
  }

  isControlInvalid(control: FormControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  isSubmitDisabled(): boolean {
    if (this.state() !== 'input') {
      return false;
    }

    const formValue = this.form.getRawValue();
    return formValue.lastName === this.customerService.customer().contactInfo.lastName
      && formValue.firstName === this.customerService.customer().contactInfo.firstName
      && formValue.middleName === this.customerService.customer().contactInfo.middleName;
  }
}
